import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearPhysicalActivities,
    deletePhysicalActivity,
    loadPhysicalActivities
} from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import { PHYSICAL_ACTIVITIES_ERROR } from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import CreatePhysicalActivity from './CreatePhysicalActivity';
import PhysicalActivityRow from './PhysicalActivityRow';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'eventDate', numeric: false, label: 'Date' },
    {
        id: 'vigorousActivityMinutes',
        numeric: true,
        label: 'Vigorous activity in past seven days (minutes)',
        align: 'center'
    },
    {
        id: 'moderateActivityMinutes',
        numeric: true,
        label: 'Moderate activity in past seven days (minutes)',
        align: 'center'
    },
    {
        id: 'walkingMinutes',
        numeric: true,
        label: 'Walking in past seven days (minutes)',
        align: 'center'
    }
];
const initialRowMetaData = {
    order: 'asc',
    orderBy: 'eventDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const PhysicalActivity = ({ participant, isDisabled = true, isDeleteDisabled = true }) => {
    const dispatch = useDispatch();
    const physicalActivityRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const physicalActivities = useSelector(
        (state) => state.entities.participantService.physicalActivities
    );
    const physicalActivitiesMetaData = useSelector(
        (state) => state.entities.participantService.physicalActivitiesMetaData
    );

    // USE EFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        participant?.id && dispatch(loadPhysicalActivities(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (physicalActivitiesMetaData?.loaded) {
            if (!physicalActivities?.length) {
                rows.length > 0 && setRows([]);
                setLoadingContentMessage(PHYSICAL_ACTIVITIES_ERROR);
            } else setRows([...physicalActivities]);
        }
    }, [physicalActivities, physicalActivitiesMetaData]);

    // FNs
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <PhysicalActivityRow
                key={el.id}
                row={el}
                isDisabled={isDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
            />
        ));

    const cleanUp = () => {
        dispatch(clearPhysicalActivities());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deletePhysicalActivity(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(physicalActivityRef);
    };

    // RENDER
    return (
        <div
            className={local.wellBeingSection}
            data-testid="physical_activity"
            ref={physicalActivityRef}>
            <h2 className={app.sectionHeading}>Physical Activity</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addPhysicalActivityButton"
                        type="button"
                        content="Add New Physical Activity"
                        disabled={isDisabled}
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <CreatePhysicalActivity
                        participant={participant}
                        onClose={onClose}
                        isDisabled={isDisabled}
                        formType={formType}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

PhysicalActivity.propTypes = {
    participant: PropTypes.object.isRequired,
    isDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool
};

export default PhysicalActivity;
